// src/components/section/MyCouponsSection.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLiffProfile } from "../../context/LiffProfileContext";
import moment from "moment-timezone";
import myCouponIcon from "../../assets/images/mycoupon2-icon.png";
import bigCouponIcon from "../../assets/images/mycoupon-icon.png";

/* ======= CONST / MAPS ยกมาจากหน้า MyCoupons ======= */
const ALL_COUPON_TYPE_VALUE = "B,M,P";
const BIG_C_COUPON_TYPE_VALUE = "B,M";
const PARTNER_COUPON_TYPE_VALUE = "P";

const NEW_MEMBER_COUPON_CATEGORY = "11";
const PREMIUM_COUPON_CATEGORY = "12";
const SPECIAL_FOR_YOU_COUPON_CATEGORY = "13";

const couponStatusGroup = {
  [BIG_C_COUPON_TYPE_VALUE]: [
    {
      key: "all",
      couponStatus: "I",
      excludedCouponCategories: [
        NEW_MEMBER_COUPON_CATEGORY,
        PREMIUM_COUPON_CATEGORY,
      ],
    },
    {
      key: "special_for_you",
      couponStatus: "I",
      couponCategories: [SPECIAL_FOR_YOU_COUPON_CATEGORY],
    },
    {
      key: "new_member",
      couponStatus: "I",
      couponCategories: [NEW_MEMBER_COUPON_CATEGORY],
    },
    { key: "used_expired", couponStatus: "E,U" },
  ],
  [PARTNER_COUPON_TYPE_VALUE]: [
    {
      key: "all",
      couponStatus: "I",
      excludedCouponCategories: [
        NEW_MEMBER_COUPON_CATEGORY,
        PREMIUM_COUPON_CATEGORY,
      ],
    },
    {
      key: "special_for_you",
      couponStatus: "I",
      couponCategories: [SPECIAL_FOR_YOU_COUPON_CATEGORY],
    },
    { key: "used_expired", couponStatus: "E,U" },
  ],
};

/* ======= helper: วันที่ TH/EN (เดือนไทย + พ.ศ. เมื่อ i18n = th) ======= */
const formatDateTHEN = (i18n, iso) => {
  if (!iso) return "";
  const isTH = i18n?.language?.startsWith("th");
  const d = new Date(iso);
  try {
    const df = new Intl.DateTimeFormat(isTH ? "th-TH-u-ca-buddhist" : "en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Bangkok",
    });
    return df.format(d).replace(".", "");
  } catch {
    const m = moment.tz(iso, "Asia/Bangkok").locale(isTH ? "th" : "en");
    const y = m.year() + (isTH ? 543 : 0);
    return m.format("D MMM ") + y;
  }
};

/**
 * MyCouponsSection
 * แถบคูปองบนหน้า Home:
 * - ถ้าไม่ล็อกอิน -> แสดงการ์ดชวนกด "คูปองของฉัน"
 * - ถ้าล็อกอิน -> ดึงคูปองจาก API และแสดงการ์ดแนวนอน
 *
 * Props:
 * - loading: boolean         // ถ้ายังโหลด sections อื่นอยู่ให้ซ่อนไว้ก่อน
 * - loginCardIconSrc: string // ไอคอนวงกลมเขียว
 * - initialCouponType: "B,M" | "P" (default "B,M")
 * - initialCategoryKey: "all" | "special_for_you" | "new_member" (default "all")
 * - pageSize: number (default 10)
 */
export default function MyCouponsSection({
  loading = false,
  initialCouponType = ALL_COUPON_TYPE_VALUE,
  initialCategoryKey = "all",
  pageSize = 10,
}) {
  const { i18n, t } = useTranslation();
  const { authToken, userId } = useLiffProfile();
  const navigate = useNavigate();
  const isLoggedIn = !!(authToken || userId);

  const [coupons, setCoupons] = useState([]);
  const [total, setTotal] = useState(0);
  const [info, setInfo] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [params, setParams] = useState(null);
  const listRef = useRef(null);

  // headers ตามหน้า MyCoupons
  const headers = useMemo(
    () => ({
      Authorization: authToken ? `Bearer ${authToken}` : undefined,
      "X-Client-Auth": process.env.REACT_APP_CLIENT_AUTH,
      "x-user-id": userId,
      "Accept-Language": i18n.language.toUpperCase(),
    }),
    [authToken, userId, i18n.language]
  );

  // เตรียมพารามิเตอร์เริ่มต้น (เทียบ logic หน้า MyCoupons)
  useEffect(() => {
    if (!isLoggedIn) return;
    const group =
      couponStatusGroup[initialCouponType] ||
      couponStatusGroup[BIG_C_COUPON_TYPE_VALUE];
    const selected =
      group.find((g) => g.key === initialCategoryKey) || group[0];

    const next = {
      couponType: initialCouponType,
      pageNo: 1,
      pageSize,
      couponStatus: selected.couponStatus,
      couponCategories: selected.couponCategories || [],
      excludedCouponCategories: selected.excludedCouponCategories || [],
    };
    setParams(next);
  }, [isLoggedIn, initialCouponType, initialCategoryKey, pageSize]);

  // ดึงข้อมูลจาก API
  useEffect(() => {
    const fetchData = async () => {
      if (!isLoggedIn || !params) return;
      setFetching(true);
      try {
        const raw = {
          ...params,
          couponCategories: params.couponCategories?.join(","),
          excludedCouponCategories: params.excludedCouponCategories?.join(","),
        };
        const qs = new URLSearchParams();
        Object.entries(raw).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") qs.append(k, v);
        });

        const url = `${
          process.env.REACT_APP_API_URL
        }/api/users/current/clm-coupons/?${qs.toString()}`;
        const res = await axios.get(url, { headers });
        const payload = res?.data?.data || { items: [], total: 0, info: null };
        setCoupons(payload.items || []);
        setTotal(payload.total || 0);
        setInfo(payload.info || null);

        // track view
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "my_coupon_view_strip",
          user_id: userId,
          coupon_type: initialCouponType === "P" ? "partner" : "bigc",
          total: payload.total || 0,
        });
      } catch (e) {
        console.error("MyCouponsSection fetch error:", e?.message);
        setCoupons([]);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [isLoggedIn, params, headers, initialCouponType, userId]);

  if (loading) return null;

  function CouponCardSkeleton() {
    return (
      <article className="w-[300px] shrink-0 snap-start bg-white rounded-2xl border border-gray-200 p-4">
        <div className="h-[24px] bg-gray-200 rounded animate-pulse" />
        <div className="mt-2 space-y-2">
          <div className="h-[18px] bg-gray-200 rounded animate-pulse" />
          <div className="h-[18px] bg-gray-200 rounded animate-pulse w-5/6" />
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
          <div className="h-[18px] w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-[34px] w-24 bg-gray-200 rounded-full animate-pulse" />
        </div>
      </article>
    );
  }

  // ---- ยังไม่ล็อกอิน: การ์ดชวนกด ----
  if (!isLoggedIn) {
    return (
      <div className="w-[320px] mt-6 mb-2 z-10 h-[163px]">
        <div className="min-h-[150px] flex justify-center">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 px-4 py-6 w-[92%] max-w-[560px]">
            <div className="text-[16px] font-semibold text-gray-900 text-center">
              {t(
                "login_for_personal_deals",
                "เข้าสู่ระบบ เพื่อรับดีลพิเศษเฉพาะคุณ"
              )}
            </div>
            <div className="mx-2 my-6 h-px bg-gray-200" />
            <Link
              to="/my-coupons"
              className="block"
              aria-label={t("my_coupon")}
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: "home_menu_click",
                  menu_target: "/my-coupons",
                  menu_name: "my_coupon",
                  menu_index: null,
                });
              }}
            >
              <div className="relative w-fit mx-auto">
                <span className="absolute -left-3 top-1 -translate-y-1/2 w-10 h-10 rounded-full bg-[#2EC973] ring-1 ring-white shadow flex items-center justify-center">
                  <img
                    src={bigCouponIcon}
                    alt=""
                    className="w-8 h-8 object-contain"
                  />
                </span>
                <span className="pl-10 pr-10 py-2.5 bg-[#FF4D4F] text-white rounded-full font-extrabold text-[16px] shadow leading-none inline-flex items-center h-[40px]">
                  {t("my_coupon", "คูปองของฉัน")}
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>
    );
  } else {
    // ---- ล็อกอินแล้ว: แถบหัว + การ์ดแนวนอน ----
    return (
      <section className="w-full pl-6 mt-4">
        {/* Header */}
        <div className="max-w-[980px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src={myCouponIcon}
              alt="promotion"
              className="object-contain w-6 h-6"
              loading="lazy"
              decoding="async"
            />
            <h2 className="text-[20px] font-extrabold text-gray-900 leading-6 h-6">
              {t("my_coupon", "คูปองของฉัน")}
            </h2>
          </div>
          <Link
            to={`/my-coupons?couponType=${encodeURIComponent(
              initialCouponType === PARTNER_COUPON_TYPE_VALUE ? "P" : "B"
            )}&category=${initialCategoryKey}`}
            className="flex items-center cursor-pointer text-danger text-sm mr-6"
            onClick={() => {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: "home_coupon_see_more",
                target: "/my-coupons",
                couponType: initialCouponType,
                category: initialCategoryKey,
              });
            }}
          >
            {t("see_more", "ดูเพิ่มเติม")}
          </Link>
        </div>

        <div className="max-w-[980px] mx-auto">
          <div className="min-h-[190px]" aria-live="polite">
            {/* List */}

            <div
              ref={listRef}
              className="mt-3 flex flex-nowrap gap-4 overflow-x-auto no-scrollbars pl-1 snap-x snap-mandatory"
            >
              {(coupons || []).map((c, i) => {
                const title = c.title || c.name || "";
                const desc = c.description || c.detail || c.condition || "";
                const expire =
                  c.expireDate || c.expireAt || c.endDate || c.end_date;
                const to =
                  c.targetURI || c.useURI || `/my-coupons/${c.id ?? ""}`;

                // ปุ่ม "ใช้คูปอง" ถ้าประเภท P หรือ M ให้ไป modal (หน้าเต็มจัดการ) -> เราพาเข้าหน้ารายละเอียดแทน
                const handleUse = (e) => {
                  e.preventDefault();
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: "home_coupon_use_click",
                    coupon_id: c.id,
                    coupon_title: title,
                    index: i,
                    target: to,
                  });
                  // โฟลว์บนหน้า Home: นำไปหน้า detail แล้วค่อยตัดสินใจต่อ
                  // ถ้าคุณอยากเปิด modal ในหน้าแรก ให้ย้าย UseUserBcCouponModal มาใช้ที่นี่แทน
                  window.location.href = to;
                };

                return (
                  <article
                    key={`coupon-${c.id ?? i}`}
                    className="w-[300px] shrink-0 snap-start bg-white rounded-2xl border border-gray-200 shadow-sm p-4"
                  >
                    <h3 className="text-gray-900 font-semibold leading-6 h-[24px] overflow-hidden text-ellipsis whitespace-nowrap text-left">
                      {title}
                    </h3>
                    <p className="mt-1 text-gray-500 text-sm leading-5 line-clamp-2 text-left h-[40px]">
                      {desc}
                    </p>
                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-gray-500 text-sm">
                        {t("expires_on", "หมดอายุ")} :{" "}
                        {formatDateTHEN(i18n, expire)}
                      </div>
                      <a
                        href={to}
                        onClick={handleUse}
                        className="px-4 h-[34px] inline-flex items-center rounded-full text-white bg-[#FF4D4F] text-sm font-semibold hover:opacity-95"
                      >
                        {t("use_coupon", "ใช้คูปอง")}
                      </a>
                    </div>
                  </article>
                );
              })}
              {fetching && coupons.length === 0 && (
                <>
                  <CouponCardSkeleton />
                  <CouponCardSkeleton />
                </>
              )}
              {/* ปุ่มดูเพิ่มเติม */}
              {coupons.length > 0 && (
                <div className="w-[300px] sm:w-[340px] shrink-0 snap-start flex items-stretch">
                  <Link
                    to="/my-coupons"
                    className="w-full"
                    onClick={() => {
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({
                        event: "home_coupon_see_more",
                        target: "/my-coupons",
                        couponType: initialCouponType,
                        category: initialCategoryKey,
                      });
                    }}
                  >
                    <article className="h-full bg-white rounded-2xl border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm transition flex items-center justify-center">
                      <span className="text-primary font-medium text-base">
                        {t("see_more")} →
                      </span>
                    </article>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* กรณีไม่มีคูปอง (ล็อกอินแล้ว) */}
          {!fetching && coupons.length === 0 && (
            <div className="w-full text-center text-gray-500 py-6">
              {t("no_coupons", "ยังไม่มีคูปองในขณะนี้")}
            </div>
          )}
        </div>
      </section>
    );
  }
}
